import {Inject, Injectable} from "@angular/core";
import {JwtHttpClientService} from "../../http/jwt-http-client.service";
import {ConfigurationService} from "../../configuration/configuration.service";
import {ResourceModule} from "../resource.module";
import {ReportRequestData} from "./report-request.data";
import {HttpResponse} from "@angular/common/http";
import {firstValueFrom, map} from "rxjs";
import {EntityData} from "../entity/entity.data";

/**
 * Generic service for accessing a report resource from the server.
 *
 * Errors are thrown as a #DataClientError
 */
@Injectable()
export class ReportService {

    /**
     * Static URL prefix for all server side calls
     * @type {string}
     */
    public static readonly URL_PREFIX = '/dataserver/report/';

    public static readonly REQUEST_PREFIX = 'request/';

    public static readonly DOWNLOAD_PREFIX = 'download/';

    public static readonly SAVED_PREFIX = 'saved/';

    public static readonly DEFAULT_SAVED_PREFIX = 'default/';

    /**
     * Constructs the service with a resource, Http service, and Error service.
     * The resource may be injected via the #ENTITY_RESOURCE alias
     * @param http HTTP Service
     * @param configService
     * @param nameSpace NameSpace that forms part of the URL
     */
    constructor(protected http: JwtHttpClientService,
                protected configService: ConfigurationService,
                @Inject(ResourceModule.RESOURCE_NAMESPACE_KEY) protected nameSpace: string) {
    }

    /**
     * Request a report
     * @param {String} reportName
     * @param {EntityData<any>} criteria
     * @returns {Promise<ReportRequestData>} Data contains the report requested
     */
    public requestReport(reportName: String, criteria: EntityData<any>): Promise<ReportRequestData> {
        return firstValueFrom(this.http.post(ReportService.URL_PREFIX + ReportService.REQUEST_PREFIX + this.nameSpace + "/" + reportName, criteria.toJSONString()).pipe(
            map((response: HttpResponse<Object>) => <ReportRequestData> EntityData.create(ReportRequestData).load(response.body || {}))
        ));
    }

    /**
     * Retrieve a report request from the server
     * @param {String} identifier
     * @returns {Promise<ReportRequestData>}
     */
    public getReportRequest(identifier: String): Promise<ReportRequestData> {
        return firstValueFrom(this.http.get(ReportService.URL_PREFIX + ReportService.REQUEST_PREFIX + identifier).pipe(
            map((response: HttpResponse<Object>) => <ReportRequestData> EntityData.create(ReportRequestData).load(response.body || {}))
        ));
    }

    /**
     * Returns a stream for the report content from the server
     * @param {string} identifier
     * @returns {Promise<{filename: string; data: Blob}>}
     */
    public getReportStream(identifier: string): Promise<Blob> {
        return firstValueFrom(this.http.get(ReportService.URL_PREFIX + ReportService.DOWNLOAD_PREFIX + identifier, undefined, undefined, "blob").pipe(
            map((response: HttpResponse<Blob>) => response.body)
        ));
    }

    public getDefaultSavedReportParameters(reportName: string, customReportName: string): Promise<any> {
        let url: string = ReportService.URL_PREFIX + ReportService.DEFAULT_SAVED_PREFIX + this.nameSpace + "/" + reportName;
        if (customReportName) {
            url = url + "/" + customReportName;
        }

        return firstValueFrom(this.http.post(url, {}).pipe(
            map((response: HttpResponse<Object>) => response.body || undefined)
        ));
    }

    public getSavedReportParameters(reportName: string, identifier: string): Promise<any> {
        return firstValueFrom(this.http.post(ReportService.URL_PREFIX + ReportService.SAVED_PREFIX + this.nameSpace + "/" + reportName + "/" + identifier, {}).pipe(
            map((response: HttpResponse<Object>) => response.body || undefined)
        ));
    }

    /**
     * Indicates that the authenticated user has permission to request a report
     * @returns {Promise<boolean>}
     */
    public static canRequestReport(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/report/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "generate")) : false);
    }
}
